import React from 'react';
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';

/**
 * The section header used across Contact Optimization.
 *
 * Replaces ChartSectionHeader, which grew three problems as sections
 * accumulated controls:
 *
 * 1. **The subtitle ran full-bleed underneath the actions.** Title and
 *    actions sat in one grid row and the subtitle in a second, spanning
 *    the whole width -- so a long subtitle flowed under the buttons and
 *    the block read as an L rather than as two aligned columns. Here the
 *    text column is capped at a readable measure and never passes beneath
 *    the action cluster.
 *
 * 2. **Actions were an undifferentiated row.** A bordered picker next to
 *    two bare icon buttons at 0.5 spacing looked like leftovers rather
 *    than a set. They are now two named slots -- `controls` for stateful
 *    things that change what you are looking at, `utilities` for actions
 *    on what you already see -- separated by a hairline, with the icons
 *    given a matching bordered treatment so the cluster reads as one
 *    object.
 *
 * 3. **Nothing aligned.** `alignItems: flex-start` hung the controls off
 *    the title's cap height. The two columns now share a baseline row.
 *
 * Slots, left to right and top to bottom:
 *
 *   eyebrow      small uppercase scope label, optional
 *   title        the section name
 *   controls     pickers, toggles -- things that change the query
 *   utilities    download, explainer toggle -- things that act on the view
 *   subtitle     one or two lines, capped at ~78ch
 *   meta         chips or footnotes under the subtitle, optional
 *
 * `actions` is kept as a back-compatible alias for `utilities` so existing
 * call sites can migrate one at a time.
 */
export interface SectionHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  eyebrow?: React.ReactNode;
  /** Stateful controls: date pickers, grain toggles, scope selectors. */
  controls?: React.ReactNode;
  /** Actions on the current view: download, explainer toggle. */
  utilities?: React.ReactNode;
  /** @deprecated Use `utilities`. Kept so ChartSectionHeader call sites migrate gradually. */
  actions?: React.ReactNode;
  /** Chips or footnotes below the subtitle. */
  meta?: React.ReactNode;
  /** Drops the trailing divider, for headers inside an already-bounded box. */
  hideDivider?: boolean;
}

/**
 * Icon button sized and bordered to sit level with a small outlined
 * button, so a header's utilities read as one set rather than as icons
 * floating beside a control.
 */
export function HeaderIconButton({
  title,
  onClick,
  disabled,
  active,
  children,
  ariaLabel,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: React.ReactNode;
  ariaLabel: string;
}) {
  return (
    <Tooltip title={title} arrow>
      {/* span: a disabled button swallows the events Tooltip needs. */}
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          aria-label={ariaLabel}
          sx={(t) => ({
            width: 30,
            height: 30,
            borderRadius: `${t.ds.radius.sm}px`,
            border: `1px solid ${active ? t.palette.primary.main : t.tokens.border.subtle}`,
            color: active ? t.palette.primary.main : t.palette.text.secondary,
            bgcolor: active ? `${t.palette.primary.main}0F` : 'transparent',
            '&:hover': { bgcolor: t.tokens.surface.sunken ?? 'action.hover' },
          })}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default function SectionHeader({
  title,
  subtitle,
  eyebrow,
  controls,
  utilities,
  actions,
  meta,
  hideDivider,
}: SectionHeaderProps) {
  const util = utilities ?? actions;
  const hasCluster = Boolean(controls || util);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: 'grid',
          // Grid rather than a wrapping flex row: a single item on a
          // space-between line has nothing to space itself from, so a
          // wrapped action cluster would jump to the left edge. Columns
          // keep it pinned right whatever the title does.
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          columnGap: 2.5,
          alignItems: 'center',
          minHeight: hasCluster ? 30 : undefined,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                fontSize: '0.62rem',
                letterSpacing: 0.7,
                textTransform: 'uppercase',
                mb: 0.25,
              }}
              color="text.disabled"
            >
              {eyebrow}
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
            {title}
          </Typography>
        </Box>

        {hasCluster && (
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexShrink: 0 }}>
            {controls && (
              <Stack direction="row" alignItems="center" spacing={1}>
                {controls}
              </Stack>
            )}
            {controls && util && (
              <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
            )}
            {util && (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                {util}
              </Stack>
            )}
          </Stack>
        )}
      </Box>

      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mt: 0.75,
            // Capped so the line never runs under the action cluster and
            // stays inside a comfortable reading measure.
            maxWidth: '78ch',
            lineHeight: 1.55,
          }}
        >
          {subtitle}
        </Typography>
      )}

      {meta && (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
          {meta}
        </Stack>
      )}

      {!hideDivider && <Divider sx={{ mt: 1.5 }} />}
    </Box>
  );
}
